/**
 * A member's profile as a stylist reads it: the labelled groups on the member
 * page's Profile tab and beside an outfit under review are built here, so a
 * stylist meets the same facts in the same words wherever they look.
 */

import type { ReactNode } from 'react';
import type { MemberDetail } from '@closet-theory/api';
import { NOT_PROVIDED, accountStatus, humanize, relativeTime, wording } from './member-format';
import { Card, DetailList } from './member-ui';

export type ProfileItems = Array<[ReactNode, ReactNode]>;

export type ProfileGroupSpec = { title: string; items: ProfileItems };

export function textOf(
  record: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function wordsOf(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values
    .filter((item): item is string => typeof item === 'string')
    .map(wording)
    .filter(Boolean);
}

/** One answer, or "Not provided yet" when the member has not given it. */
function answer(value: unknown): string {
  const words = wordsOf(value);
  return words.length ? words.join(', ') : NOT_PROVIDED;
}

/** The onboarding answers in `style_profile`, in the order a stylist reads them. */
const STYLE_ANSWERS = {
  fits_loved: 'Fits they love',
  colours_loved: 'Colours they love',
  colours_avoided: 'Colours to avoid',
  brands_liked: 'Brands they like',
  brands_avoided: 'Brands to avoid',
  gender_presentation: 'Clothing section',
} as const;

const LIFE_ANSWERS = {
  shopping_frequency: 'How often they shop',
  work_dress: 'What they wear to work',
  goal: 'What they want from styling',
  frustration: 'Biggest frustration',
} as const;

function answers(
  record: Record<string, unknown> | null | undefined,
  labels: Record<string, string>,
): ProfileItems {
  return Object.entries(labels).map(([key, label]) => [label, answer(record?.[key])]);
}

/**
 * Everything the member read says about who this member is, grouped and
 * labelled. Only questions onboarding asks appear; an unanswered one says so.
 */
export function profileGroups(detail: MemberDetail): ProfileGroupSpec[] {
  const pinned = detail.pinned_profile_summary ?? {};
  const sizes = pinned.size_summary;
  const style = detail.style_profile;
  const onboarding = detail.onboarding_session;
  const address = [textOf(detail.user, 'apartment'), textOf(detail.user, 'building')]
    .filter(Boolean)
    .join(', ');
  return [
    {
      title: 'Style',
      items: [
        ['Style type', answer(pinned.style_archetype?.split(','))],
        ['Fits to avoid', answer(pinned.exclusions)],
        ...answers(style, STYLE_ANSWERS),
      ],
    },
    {
      title: 'Sizes and body',
      items: [
        ['Top size', sizes?.top?.trim() || NOT_PROVIDED],
        ['Bottom size', sizes?.bottom?.trim() || NOT_PROVIDED],
        ['Body shape', answer(textOf(detail.body_profile, 'body_shape'))],
      ],
    },
    {
      title: 'Shopping and work',
      items: [
        ['Spend per shopping trip', answer(pinned.budget?.tier)],
        ...answers(style, LIFE_ANSWERS),
      ],
    },
    {
      title: 'Account',
      items: [
        ['Account', accountStatus(textOf(detail.user, 'status'))],
        [
          'Onboarding',
          onboarding
            ? `${humanize(textOf(onboarding, 'status')) || 'Started'}${
                typeof onboarding.current_step === 'string' && onboarding.status !== 'completed'
                  ? ` · at ${humanize(onboarding.current_step)}`
                  : ''
              }`
            : NOT_PROVIDED,
        ],
        ['Address', address || NOT_PROVIDED],
      ],
    },
  ];
}

/** "Profile updated 3 days ago", from when the member's answers last changed. */
export function profileUpdated(detail: MemberDetail, now: Date = new Date()): string | undefined {
  const version = detail.pinned_profile_summary?.source_version;
  if (!version || Number.isNaN(Date.parse(version))) return undefined;
  const when = relativeTime(version, now);
  if (when === 'Just now') return 'Profile updated just now';
  return `Profile updated ${/ago$/.test(when) ? when : `on ${when}`}`;
}

export function ProfileGroup({
  title,
  items,
  dense,
}: ProfileGroupSpec & { dense?: boolean | undefined }) {
  return (
    <Card>
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
      <DetailList items={items} dense={dense} />
    </Card>
  );
}
